// src/components/brandCustomization/TicketCustomization/TicketCustomizationForm.jsx
import React from 'react';
import { FaCreditCard } from 'react-icons/fa';
import { CustomizationCard, SaveResetButtons, TextInput } from '../shared';
import TicketStyleEditor from './TicketStyleEditor';
import TicketLayoutSelector from './TicketLayoutSelector';
import TicketLayoutOptions from './TicketLayoutOptions';
import TicketHeroImageUpload from './TicketHeroImageUpload';
import ticketCustomizationService from '../../../helpers/ticketCustomizationHelper';

/**
 * TicketCustomizationForm - Formulario completo para personalizar la tarjeta de puntos
 */
const TicketCustomizationForm = ({
    formData,
    onUpdate,
    onToggleActive,
    onSave,
    onReset,
    onPreview,
    isSaving,
    marcaId,
    onRefresh,
    onFetchHeroImageOnly
}) => {
    const handleTextChange = (field, value) => {
        onUpdate(`texts.${field}`, value);
    };

    const handleStyleChange = (field, value) => {
        onUpdate(`styles.${field}`, value);
    };

    const handleLayoutChange = (layout) => {
        onUpdate('layout', layout);
    };

    return (
        <CustomizationCard
            title="Tarjeta de Fidelización"
            description="Personaliza la apariencia y textos de la tarjeta de puntos de tus clientes"
            icon={FaCreditCard}
            isActive={formData.isActive}
            onToggleActive={onToggleActive}
        >
            {/* Selección de Modelo/Plantilla */}
            <TicketLayoutSelector
                selectedLayout={formData.layout || 'wallet'}
                onChange={handleLayoutChange}
            />

            {/* Opciones de Layout */}
            <TicketLayoutOptions
                layout={formData.layout || 'wallet'}
                layoutOptions={formData.layoutOptions || {}}
                onChange={onUpdate}
            />

            {/* Imagen Hero para layout dark */}
            {formData.layout === 'dark' && (
                <TicketHeroImageUpload
                    heroImage={formData.heroImage}
                    marcaId={marcaId}
                    onUpload={ticketCustomizationService.uploadHeroImage}
                    onDelete={ticketCustomizationService.deleteHeroImage}
                    onRefresh={onRefresh}
                    onFetchHeroImageOnly={onFetchHeroImageOnly}
                    onUpdateHeroImage={(newHeroImage) => onUpdate('heroImage', newHeroImage)}
                />
            )}

            {/* Formulario de Textos */}
            <div className="mb-6 mt-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Textos Personalizables</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextInput
                        label="Título de la Tarjeta"
                        value={formData.texts?.cardTitle || ''}
                        onChange={(value) => handleTextChange('cardTitle', value)}
                        placeholder="Tarjeta de Fidelización"
                    />

                    <TextInput
                        label="Label de Puntos"
                        value={formData.texts?.pointsLabel || ''}
                        onChange={(value) => handleTextChange('pointsLabel', value)}
                        placeholder="puntos"
                    />

                    <TextInput
                        label="Label Personalizada de Puntos"
                        value={formData.texts?.pointsLabelCustom || ''}
                        onChange={(value) => handleTextChange('pointsLabelCustom', value)}
                        placeholder="ej: empanada coin"
                    />

                    <TextInput
                        label="Texto 'Te faltan'"
                        value={formData.texts?.missingPointsText || ''}
                        onChange={(value) => handleTextChange('missingPointsText', value)}
                        placeholder="Te faltan"
                    />

                    <TextInput
                        label="Texto 'Puntos disponibles'"
                        value={formData.texts?.pointsAvailableText || ''}
                        onChange={(value) => handleTextChange('pointsAvailableText', value)}
                        placeholder="puntos disponibles"
                    />

                    <TextInput
                        label="Texto 'Puntos para canjear'"
                        value={formData.texts?.pointsToRedeemText || ''}
                        onChange={(value) => handleTextChange('pointsToRedeemText', value)}
                        placeholder="¡Tienes {points} puntos para canjear!"
                        className="md:col-span-2"
                    />

                    <TextInput
                        label="Texto 'Acumula puntos'"
                        value={formData.texts?.accumulatePointsText || ''}
                        onChange={(value) => handleTextChange('accumulatePointsText', value)}
                        placeholder="Acumula puntos para canjear premios"
                        className="md:col-span-2"
                    />

                    <TextInput
                        label="Texto 'Escanea para acumular'"
                        value={formData.texts?.scanToAccumulateText || ''}
                        onChange={(value) => handleTextChange('scanToAccumulateText', value)}
                        placeholder="Escanea para acumular puntos"
                        className="md:col-span-2"
                    />

                    <TextInput
                        label="Texto 'Presenta este código'"
                        value={formData.texts?.presentCodeText || ''}
                        onChange={(value) => handleTextChange('presentCodeText', value)}
                        placeholder="Presenta este código en la tienda para acumular puntos automáticamente"
                        className="md:col-span-2"
                    />

                    <TextInput
                        label="Label 'Miembro'"
                        value={formData.texts?.memberLabel || ''}
                        onChange={(value) => handleTextChange('memberLabel', value)}
                        placeholder="Miembro"
                    />

                    <TextInput
                        label="Label 'Tienda'"
                        value={formData.texts?.storeLabel || ''}
                        onChange={(value) => handleTextChange('storeLabel', value)}
                        placeholder="Tienda"
                    />

                    <TextInput
                        label="Label 'Usuario'"
                        value={formData.texts?.userNameLabel || ''}
                        onChange={(value) => handleTextChange('userNameLabel', value)}
                        placeholder="usuario"
                    />

                    <TextInput
                        label="Texto 'Ver en Google Maps'"
                        value={formData.texts?.viewOnMapsText || ''}
                        onChange={(value) => handleTextChange('viewOnMapsText', value)}
                        placeholder="Ver en Google Maps"
                    />

                    <TextInput
                        label="Texto 'Compartir Tarjeta'"
                        value={formData.texts?.shareCardText || ''}
                        onChange={(value) => handleTextChange('shareCardText', value)}
                        placeholder="Compartir Tarjeta"
                    />

                    <TextInput
                        label="Texto 'Ticket generado el'"
                        value={formData.texts?.ticketGeneratedText || ''}
                        onChange={(value) => handleTextChange('ticketGeneratedText', value)}
                        placeholder="Ticket generado el"
                    />

                    <TextInput
                        label="Texto 'Premios disponibles'"
                        value={formData.texts?.rewardsAvailableText || ''}
                        onChange={(value) => handleTextChange('rewardsAvailableText', value)}
                        placeholder="Premios disponibles para canjear"
                    />

                    <TextInput
                        label="Título al Compartir"
                        value={formData.texts?.shareTitle || ''}
                        onChange={(value) => handleTextChange('shareTitle', value)}
                        placeholder="Mi Tarjeta de Fidelización"
                    />

                    <TextInput
                        label="Texto al Compartir"
                        value={formData.texts?.shareText || ''}
                        onChange={(value) => handleTextChange('shareText', value)}
                        placeholder="Tarjeta de {{brandName}}"
                    />
                </div>
            </div>

            {/* Editor de Estilos */}
            <TicketStyleEditor
                styles={formData.styles || {}}
                onChange={handleStyleChange}
            />

            {/* Botones de acción */}
            <SaveResetButtons
                onSave={onSave}
                onReset={onReset}
                onPreview={onPreview}
                isSaving={isSaving}
                hasChanges={true}
            />
        </CustomizationCard>
    );
};

export default TicketCustomizationForm;
